import { FC, useEffect, useMemo, useState } from 'react';
import { defaults } from 'chart.js/auto';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
	CardsContainer,
	Container,
	Field,
	FullLine,
	LeftHalf,
	LoadingWrapper,
	RightHalf,
	SectionDashboard,
} from './DashboardDealer.styles';
import { DecoratedTitle, HighlightCard, Dropdown, Loading, ErrorScreen } from '../../components';
import { Option } from '../../components/dropdown/Dropdown';
import { TDashboardDealer } from '../../services/dashboard/dashboardDealer.types';
import { getDashboardDealer } from '../../services/dashboard';
import { graphColors } from '../../utils/constants';
import { useUserStore } from '../../stores';
import { Link } from 'react-router-dom';

defaults.maintainAspectRatio = false;
defaults.responsive = true;
defaults.plugins.title.display = true;
defaults.plugins.title.align = 'start';
defaults.plugins.title.color = 'black';

const DashboardDealer: FC = () => {
	const { user } = useUserStore();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [dashboardData, setDashboardData] = useState<TDashboardDealer>();

	const dealerOptions = useMemo(() => {
		const concessionarias = user?.concessionarias || [];
		//const sortedConcessionarias = concessionarias.filter(obj=> obj.tagAtivo === '1').sort((a, b) => {
		const sortedConcessionarias = concessionarias.sort((a, b) => {
			const fantasiaA = a.fantasia || '';
			const fantasiaB = b.fantasia || '';
			return fantasiaA.localeCompare(fantasiaB);
		});
	
		const options = sortedConcessionarias.map((item) => ({
			value: item.id.toString(),
			label: item.fantasia || '',
		}));
	
		const fixedValue = concessionarias.map((item) => item.id).join(',');
		options.unshift({ value: fixedValue || '0', label: 'Todas as Concessionárias' });
	
		return options;
	}, [user?.concessionarias]);
	

	const [selectedOption, setSelectedOption] = useState<Option>(
		dealerOptions.length > 0
			? dealerOptions[0]
			: { value: 0, label: 'Nenhuma concessionária relacionada' }
	);

	const handleSelectOption = (option: Option) => {
		setSelectedOption(option);
	};

	useEffect(() => {
		setLoading(true);

		const fetchData = async () => {
			if (user?.id) {
				const dashboardFetched = await getDashboardDealer({
					ids: selectedOption.value.toString(),
				});

				if (!dashboardFetched) {
					setError(true);
					return;
				}

				setDashboardData(dashboardFetched);
			}
		};

		fetchData().finally(() =>
			setTimeout(() => {
				setLoading(false);
			}, 500)
		);
	}, [selectedOption, user]);

	return (
		<Container>
			<FullLine>
				<Field>
					<Dropdown
						options={dealerOptions}
						selected={selectedOption}
						onSelect={handleSelectOption}
					/>
				</Field>
			</FullLine>
			{loading && (
				<LoadingWrapper>
					<Loading />
				</LoadingWrapper>
			)}
			{!loading && error && (
				<LoadingWrapper>
					<ErrorScreen />
				</LoadingWrapper>
			)}
			{!loading && dashboardData && (
				<>
					<FullLine>
						<DecoratedTitle text='Dashboard' variant='h2' />
						<CardsContainer>
							<Link to='/anuncios'>
								<HighlightCard
									variant='dashboard'
									label='Anúncios Ativos'
									quantity={dashboardData?.totalizadores.anunciosAtivos}
								/>
							</Link>
							<Link to='/anuncios'>
								<HighlightCard
									variant='dashboard'
									label='Em cotação'
									quantity={dashboardData?.totalizadores.anunciosCotacao}
								/>
							</Link>
							<Link to='/anuncios'>
								<HighlightCard
									variant='dashboard'
									label='Total Vendidos'
									quantity={dashboardData?.totalizadores.anunciosVendidos as string}
								/>
							</Link>
							<Link to='/anuncios'>
								<HighlightCard
									variant='dashboard'
									label='Encerrando hoje'
									quantity={dashboardData?.totalizadores.anunciosEncerrandoHoje}
								/>
							</Link>
							<HighlightCard
								variant='dashboard'
								label='Lojistas na plataforma'
								quantity={dashboardData?.totalizadores.lojistasNaPlataforma}
							/>
						</CardsContainer>
						
					</FullLine>
					<LeftHalf>
						<SectionDashboard>
							<Bar
								data={{
									labels: dashboardData.primeiroGraficoModel.map((item) => item.periodo),
									datasets: [
										{
											label: 'Anunciados',
											data: dashboardData.primeiroGraficoModel.map((item) => item.anunciado),
											backgroundColor: 'rgba(60,141,188,0.9)',
											borderColor: 'rgba(60,141,188,0.8)',
											borderRadius: 5,
										},
										{
											label: 'Vendidos',
											data: dashboardData.primeiroGraficoModel.map((item) => item.vendido),
											backgroundColor: 'rgba(210, 214, 222, 1)',
											borderColor: 'rgba(210, 214, 222, 1)',
											borderRadius: 5,
										},
										{
											label: 'Em cotação',
											data: dashboardData.primeiroGraficoModel.map((item) => item.cotacao),
											backgroundColor: '#567B92',
											borderColor: '#567B92',
											borderRadius: 5,
										},
									],
								}}
								options={{
									plugins: {
									  subtitle: {
										display: true,
										text: 'Últimos 12 meses',
										position: 'bottom',
										align: 'end',
										color: 'red'
									  },
									},
								}}
							/>
						</SectionDashboard>
					</LeftHalf>
					<RightHalf>
						<SectionDashboard>
							<Doughnut
								data={{
									labels: dashboardData.segundoGraficoModel.map((item) => item.veiculoMarca.trim()),
									datasets: [
										{
											label: 'Qtde',
											data: dashboardData.segundoGraficoModel.map((item) => item.quantidade),
											backgroundColor: graphColors.slice(
												0,
												dashboardData.segundoGraficoModel.length
											),
											borderColor: graphColors.slice(0, dashboardData.segundoGraficoModel.length),
										},
									],
								}}
								options={{
									plugins: {
										title: {
											text: 'Veículos Vendidos/Marca',
										},
										legend: {
											position: 'right',
											labels: {
												font: {
													family: "'Inter', sans-serif",
												},
											},
										},
										subtitle: {
											display: true,
											text: 'Últimos 12 meses',
											position: 'bottom',
											align: 'end',
											color: 'red'
										},
									},
									elements: {
										line: {
											tension: 0.5,
										},
									},
								}}
							/>
						</SectionDashboard>
					</RightHalf>
					<FullLine>
						<Line
							data={{
								labels: dashboardData.terceiroGraficoModel.map((item) => item.periodo),
								datasets: [
									{
										label: 'Visualizações dos Anúncios',
										backgroundColor: 'rgba(255, 102, 0)',
										borderColor: 'rgba(255, 102, 0)',
										data: dashboardData.terceiroGraficoModel.map((item) => item.visualizacoes),
									},
								],
							}}
							options={{
								plugins: {
								  subtitle: {
									display: true,
									text: 'Últimos 12 meses',
									position: 'bottom',
									align: 'end',
									color: 'red'
								  },
								},
							}}
						/>
					</FullLine>
				</>
			)}
		</Container>
	);
};

export default DashboardDealer;
